/**
 * Review Queue
 *
 * Consolidated admin view of all scanned books requiring attention:
 * - turn_codes_pending: published by agent, awaiting turn-code review
 * - needs_review: flagged for content editing
 *
 * Subsumes the former "Turn Codes" tab. Provides inline turn-code editing,
 * approval, and navigation to the content editor.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, Badge, Button, Spinner, EmptyState } from '@/components/ui';
import { parseTurnCode, getHexColor, STANDARD_COLORS, type TurnColorMapping } from '@/lib/turn-color';
import { toast } from 'sonner';
import {
  BookOpen,
  CheckCircle,
  AlertTriangle,
  Search,
  RefreshCw,
  Wand2,
  Pencil,
  Check,
  X,
  RotateCcw,
  ExternalLink,
  Lightbulb,
  Loader2,
  Send,
} from 'lucide-react';

import {
  getBookForReview,
  getBookTurnCodeStatus,
  saveTurnCode,
  approveBook,
  suggestTurnCode,
  suggestSkillId,
  type PendingBook,
  type BookTurnCodeReport,
  type WordTurnCodeStatus,
  type SkillOption,
} from '@/services/scan/turn-code-review';
import { supabase } from '@/lib/supabase/client';
import { FEATURES } from '@/lib/feature-flags';

// =============================================================================
// TYPES
// =============================================================================

type FilterTab = 'all' | 'turn_codes_pending' | 'needs_review';

interface ReviewBook extends PendingBook {
  report?: BookTurnCodeReport;
  reportLoading?: boolean;
  superadminApproved?: boolean;
  lexiconApprovedAt?: string | null;
  turnCodesApprovedAt?: string | null;
  renderApprovedAt?: string | null;
}

// =============================================================================
// DATA FETCHING
// =============================================================================

async function fetchReviewBooks(): Promise<PendingBook[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from('scanned_books')
    .select('id, title, author, isbn, cover_image_url, created_at, quality_status, content_source, generation_metadata, superadmin_approved, lexicon_approved_at, turn_codes_approved_at, render_approved_at')
    .in('quality_status', ['turn_codes_pending', 'needs_review', 'ready'])
    .eq('superadmin_approved', false)
    .is('deleted_at', null)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[ReviewQueue] Error fetching books:', error);
    return [];
  }

  return (data || []).map((b: Record<string, unknown>) => ({
    id: b.id as string,
    title: b.title as string,
    author: b.author as string | null,
    isbn: b.isbn as string | null,
    coverImageUrl: b.cover_image_url as string | null,
    createdAt: b.created_at as string,
    qualityStatus: (b.quality_status as string) ?? undefined,
    contentSource: (b.content_source as string) ?? null,
    generationMetadata: b.generation_metadata ?? null,
    superadminApproved: (b.superadmin_approved as boolean) ?? false,
    lexiconApprovedAt: (b.lexicon_approved_at as string) ?? null,
    turnCodesApprovedAt: (b.turn_codes_approved_at as string) ?? null,
    renderApprovedAt: (b.render_approved_at as string) ?? null,
  }));
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ReviewQueuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [books, setBooks] = useState<ReviewBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterTab, setFilterTab] = useState<FilterTab>(
    (searchParams.get('filter') as FilterTab) || 'all'
  );
  const colorMappings = STANDARD_COLORS;

  const [selectedBookId, setSelectedBookId] = useState<string | null>(
    searchParams.get('book') || null
  );
  const [lexiconBookId, setLexiconBookId] = useState<string | null>(null);
  const [lexiconWords, setLexiconWords] = useState<{ word: string; frequency: number; in_turn_lexicon: boolean; turn_code: string | null }[]>([]);
  const [lexiconLoading, setLexiconLoading] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [editingLexWord, setEditingLexWord] = useState<string | null>(null);
  const [editLexValue, setEditLexValue] = useState('');
  const [addWordValue, setAddWordValue] = useState('');
  const [flaggedWords, setFlaggedWords] = useState<Map<string, { reason: string; suggestion: string }>>(new Map());
  const [flaggingInProgress, setFlaggingInProgress] = useState(false);

  const handleViewLexicon = async (bookId: string) => {
    setLexiconBookId(bookId);
    setLexiconLoading(true);
    setEditingLexWord(null);
    setAddWordValue('');
    setFlaggedWords(new Map());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data } = await (supabase as any)
      .from('book_lexicon')
      .select('word, frequency, in_turn_lexicon, turn_code')
      .eq('book_id', bookId)
      .order('word');
    const words = data || [];
    setLexiconWords(words);
    setLexiconLoading(false);

    // Auto-run AI review in background
    if (words.length > 0) {
      setFlaggingInProgress(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          const res = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/review-lexicon-ai`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ words: words.map((w: { word: string }) => w.word) }),
            }
          );
          if (res.ok) {
            const { flagged } = await res.json();
            const map = new Map<string, { reason: string; suggestion: string }>();
            for (const f of flagged || []) {
              map.set(f.word, { reason: f.reason, suggestion: f.suggestion });
            }
            setFlaggedWords(map);
          }
        }
      } catch {
        // AI review failed silently — non-blocking
      }
      setFlaggingInProgress(false);
    }
  };

  const handleDeleteLexWord = async (word: string) => {
    if (!lexiconBookId) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any).from('book_lexicon').delete().eq('book_id', lexiconBookId).eq('word', word);
    setLexiconWords(prev => prev.filter(w => w.word !== word));
    toast.success(`Deleted "${word}"`);
  };

  const handleEditLexWord = async (oldWord: string) => {
    if (!lexiconBookId || !editLexValue.trim()) return;
    const newWord = editLexValue.trim().toLowerCase();
    // Check if new word is in turn lexicon
    const { data: match } = await supabase.from('turn_color_words').select('word, turn_code').eq('word', newWord).maybeSingle();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any).from('book_lexicon').delete().eq('book_id', lexiconBookId).eq('word', oldWord);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any).from('book_lexicon').upsert({
      book_id: lexiconBookId, word: newWord, frequency: 1,
      in_turn_lexicon: !!match, turn_code: match?.turn_code || null,
    }, { onConflict: 'book_id,word' });
    setLexiconWords(prev => prev.filter(w => w.word !== oldWord).concat({
      word: newWord, frequency: 1, in_turn_lexicon: !!match, turn_code: match?.turn_code || null,
    }).sort((a, b) => a.word.localeCompare(b.word)));
    setEditingLexWord(null);
    setEditLexValue('');
    toast.success(`Updated "${oldWord}" → "${newWord}"`);
  };

  const handleAddLexWord = async () => {
    if (!lexiconBookId || !addWordValue.trim()) return;
    const word = addWordValue.trim().toLowerCase();
    if (lexiconWords.some(w => w.word === word)) { toast.error('Word already exists'); return; }
    const { data: match } = await supabase.from('turn_color_words').select('word, turn_code').eq('word', word).maybeSingle();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any).from('book_lexicon').insert({
      book_id: lexiconBookId, word, frequency: 1,
      in_turn_lexicon: !!match, turn_code: match?.turn_code || null,
    });
    setLexiconWords(prev => [...prev, {
      word, frequency: 1, in_turn_lexicon: !!match, turn_code: match?.turn_code || null,
    }].sort((a, b) => a.word.localeCompare(b.word)));
    setAddWordValue('');
    toast.success(`Added "${word}"`);
  };

  const handleApproveStep = async (bookId: string, step: 'lexicon' | 'turn_codes' | 'render') => {
    setApprovingId(bookId);
    const col = step === 'lexicon' ? 'lexicon_approved_at'
              : step === 'turn_codes' ? 'turn_codes_approved_at'
              : 'render_approved_at';
    const now = new Date().toISOString();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any)
      .from('scanned_books')
      .update({ [col]: now })
      .eq('id', bookId);
    if (error) { toast.error(error.message); setApprovingId(null); return; }

    // On lexicon approval: auto-add missing words to the main Turn lexicon
    if (step === 'lexicon') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: missingWords } = await (supabase as any)
        .from('book_lexicon')
        .select('word')
        .eq('book_id', bookId)
        .eq('in_turn_lexicon', false);

      if (missingWords?.length) {
        const newWords = missingWords.map((w: { word: string }) => ({
          word: w.word,
          length: w.word.length,
          notes: `Added from book lexicon approval`,
        }));

        // Batch upsert — ignoreDuplicates in case word was added between load and approval
        const BATCH = 200;
        let added = 0;
        for (let i = 0; i < newWords.length; i += BATCH) {
          const batch = newWords.slice(i, i + BATCH);
          const { data: inserted } = await supabase
            .from('turn_color_words')
            .upsert(batch, { onConflict: 'word', ignoreDuplicates: true })
            .select('id');
          added += inserted?.length || 0;
        }

        // Update book_lexicon to mark these words as now in the lexicon
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabase as any)
          .from('book_lexicon')
          .update({ in_turn_lexicon: true })
          .eq('book_id', bookId)
          .eq('in_turn_lexicon', false);

        if (added > 0) {
          toast.success(`${added} new words added to Turn Lexicon`);
        }
      }
    }

    // Update local state
    const book = books.find(b => b.id === bookId);
    const updated = { ...book,
      lexiconApprovedAt: step === 'lexicon' ? now : book?.lexiconApprovedAt,
      turnCodesApprovedAt: step === 'turn_codes' ? now : book?.turnCodesApprovedAt,
      renderApprovedAt: step === 'render' ? now : book?.renderApprovedAt,
    };

    // If all 3 steps approved, set superadmin_approved
    if (updated.lexiconApprovedAt && updated.turnCodesApprovedAt && updated.renderApprovedAt) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase as any).from('scanned_books').update({ superadmin_approved: true }).eq('id', bookId);
      updated.superadminApproved = true;
      toast.success('All steps approved — book is live!');
    } else {
      const stepName = step === 'lexicon' ? 'Lexicon' : step === 'turn_codes' ? 'Turn Codes' : 'Render';
      toast.success(`${stepName} approved`);
    }

    setBooks(prev => prev.map(b => b.id === bookId ? { ...b, ...updated } as ReviewBook : b));
    setApprovingId(null);
  };

  const handleSuperAdminApprove = async (bookId: string) => {
    setApprovingId(bookId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any)
      .from('scanned_books')
      .update({ superadmin_approved: true })
      .eq('id', bookId);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Book approved by SuperAdmin');
      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, superadminApproved: true } : b));
    }
    setApprovingId(null);
  };

  const renderLexiconModal = () => {
    if (!lexiconBookId) return null;
    const lexiconBook = books.find(b => b.id === lexiconBookId);
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60" onClick={() => setLexiconBookId(null)}>
        <div className="bg-white rounded-2xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col shadow-xl border border-[#D4D4D4]" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between p-4 border-b border-[#D4D4D4]">
            <div>
              <h3 className="font-semibold text-[#1A1A1A]">{lexiconBook?.title || 'Book'} — Lexicon</h3>
              <p className="text-xs text-[#888888]">
                {lexiconWords.length} unique words &middot;{' '}
                {lexiconWords.filter(w => w.in_turn_lexicon).length} in Turn Lexicon &middot;{' '}
                {lexiconWords.filter(w => !w.in_turn_lexicon).length} not in lexicon
                {flaggingInProgress && <span className="ml-1">· AI reviewing...</span>}
                {!flaggingInProgress && flaggedWords.size > 0 && <span className="ml-1 text-[#FF3B30]">· {flaggedWords.size} flagged by AI</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {lexiconBookId && !(books.find(b => b.id === lexiconBookId) as ReviewBook)?.lexiconApprovedAt && (
                <button
                  onClick={() => { handleApproveStep(lexiconBookId, 'lexicon'); setLexiconBookId(null); }}
                  disabled={approvingId === lexiconBookId}
                  className="px-3 py-1.5 rounded-full bg-[#111] text-white text-xs font-medium hover:bg-[#333] disabled:opacity-30"
                >
                  Approve Lexicon
                </button>
              )}
              <button onClick={() => setLexiconBookId(null)} className="px-3 py-1.5 rounded-lg bg-black/[0.03] text-[#3D3D3D] text-xs hover:bg-black/[0.05]">Close</button>
            </div>
          </div>
          {/* Add word input */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E5E5E5]">
            <input
              type="text"
              value={addWordValue}
              onChange={e => setAddWordValue(e.target.value.toLowerCase().replace(/[^a-z'-]/g, ''))}
              placeholder="Add a word..."
              className="flex-1 px-3 py-1.5 border border-[#D4D4D4] rounded-lg text-sm font-mono text-[#1A1A1A] focus:outline-none focus:border-[#1A1A1A]"
              onKeyDown={e => { if (e.key === 'Enter') handleAddLexWord(); }}
            />
            <button
              onClick={handleAddLexWord}
              disabled={!addWordValue.trim()}
              className="px-3 py-1.5 rounded-full bg-[#111] text-white text-xs font-medium hover:bg-[#333] disabled:opacity-30"
            >
              Add
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {lexiconLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#D4D4D4]" /></div>
            ) : lexiconWords.length === 0 ? (
              <p className="text-[#888888] text-sm text-center py-8">No lexicon data. The agent may not have submitted this book yet.</p>
            ) : (
              <div className="space-y-4">
                {[true, false].map(inLexicon => {
                  const filtered = lexiconWords.filter(w => w.in_turn_lexicon === inLexicon);
                  if (filtered.length === 0) return null;
                  const color = inLexicon ? '#34C759' : '#FF9F0A';
                  const label = inLexicon ? 'In Turn Lexicon' : 'Not in Turn Lexicon';
                  return (
                    <div key={String(inLexicon)}>
                      <h4 className="text-xs font-medium mb-2" style={{ color }}>{label} ({filtered.length})</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {filtered.map(w => {
                          const flag = flaggedWords.get(w.word);
                          if (editingLexWord === w.word) return (
                            <span key={w.word} className="inline-flex items-center gap-1 px-1 py-0.5 rounded-lg border border-[#1A1A1A]">
                              <input
                                type="text"
                                value={editLexValue}
                                onChange={e => setEditLexValue(e.target.value.toLowerCase().replace(/[^a-z'-]/g, ''))}
                                className="w-24 px-1 py-0.5 text-xs font-mono border-none outline-none"
                                autoFocus
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleEditLexWord(w.word);
                                  if (e.key === 'Escape') { setEditingLexWord(null); setEditLexValue(''); }
                                }}
                              />
                              <button onClick={() => handleEditLexWord(w.word)} className="text-[#34C759] text-xs px-1">✓</button>
                              <button onClick={() => { setEditingLexWord(null); setEditLexValue(''); }} className="text-[#888] text-xs px-1">✕</button>
                            </span>
                          );
                          return (
                            <span
                              key={w.word}
                              className={`group inline-flex items-center gap-0.5 px-2 py-1 text-xs rounded-lg font-mono cursor-pointer transition-colors ${flag ? 'ring-2 ring-[#FF3B30] ring-offset-1' : ''}`}
                              style={{ background: flag ? '#FF3B3015' : `${color}15`, color: flag ? '#FF3B30' : color }}
                              title={flag ? `⚠ AI: ${flag.suggestion} (${flag.reason})` : `${w.turn_code || 'no turn code'} (×${w.frequency}) — click to edit`}
                              onClick={() => { setEditingLexWord(w.word); setEditLexValue(w.word); }}
                            >
                              {flag && <span className="mr-0.5">⚠</span>}
                              {w.word}
                              <span style={{ color: flag ? '#FF3B3080' : `${color}80` }} className="ml-0.5">×{w.frequency}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteLexWord(w.word); }}
                                className="ml-0.5 opacity-0 group-hover:opacity-100 text-[#FF3B30] transition-opacity"
                                title="Delete word"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const loadBooks = useCallback(async () => {
    const fetched = await fetchReviewBooks();
    setBooks(fetched.map(b => ({ ...b })));
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    loadBooks();
  }, [loadBooks]);

  // -------------------------------------------------------------------------
  // Report loading (for turn code detail view)
  // -------------------------------------------------------------------------

  const loadReportForBook = useCallback(async (bookId: string) => {
    setBooks(prev => prev.map(b => b.id === bookId ? { ...b, reportLoading: true } : b));
    const report = await getBookTurnCodeStatus(bookId);
    setBooks(prev => prev.map(b => b.id === bookId ? { ...b, report, reportLoading: false } : b));
  }, []);

  const handleWordSaved = useCallback((bookId: string, word: string, turnCode: string, skillId?: string) => {
    setBooks(prev => prev.map(b => {
      if (b.id !== bookId || !b.report) return b;
      const updatedWords = b.report.words.map(w =>
        w.cleanWord === word ? { ...w, turnCode, inLexicon: true, ...(skillId !== undefined && { skillId }) } : w
      );
      const uniqueClean = [...new Set(updatedWords.map(w => w.cleanWord))];
      const uniqueInLexicon = new Set(updatedWords.filter(w => w.inLexicon).map(w => w.cleanWord)).size;
      const uniqueMissing = uniqueClean.length - uniqueInLexicon;
      const coveragePct = uniqueClean.length > 0 ? Math.round((uniqueInLexicon / uniqueClean.length) * 100) : 100;
      return {
        ...b,
        report: {
          ...b.report,
          words: updatedWords,
          matchedWords: uniqueInLexicon,
          missingWords: uniqueMissing,
          coveragePct,
        },
      };
    }));
  }, []);

  // -------------------------------------------------------------------------
  // Book selection
  // -------------------------------------------------------------------------

  const handleSelectBook = useCallback((bookId: string) => {
    setSelectedBookId(bookId);
    setSearchParams(prev => {
      prev.set('book', bookId);
      return prev;
    });
    const book = books.find(b => b.id === bookId);
    if (!book?.report) {
      loadReportForBook(bookId);
    }
  }, [books, loadReportForBook, setSearchParams]);

  const handleBack = useCallback(() => {
    setSelectedBookId(null);
    setSearchParams(prev => {
      prev.delete('book');
      return prev;
    });
  }, [setSearchParams]);

  // Deep-link: if ?book=<id> is not in the queue, fetch directly
  useEffect(() => {
    const bookParam = searchParams.get('book');
    if (!bookParam || loading) return;

    const match = books.find(b => b.id === bookParam);
    if (match) {
      if (!match.report && !match.reportLoading) loadReportForBook(bookParam);
      return;
    }

    (async () => {
      const fetched = await getBookForReview(bookParam);
      if (fetched) {
        setBooks(prev => [...prev, { ...fetched }]);
        loadReportForBook(bookParam);
      }
    })();
  }, [searchParams, books, loading, loadReportForBook]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const handleApprove = useCallback(async (bookId: string) => {
    try {
      const result = await approveBook(bookId);
      if (result.success) {
        toast.success(`Book approved — ${result.parentsNotified} parent(s) notified`);
        setSelectedBookId(null);
        setSearchParams(prev => { prev.delete('book'); return prev; });
        loadBooks();
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to approve book');
    }
  }, [loadBooks, setSearchParams]);

  const handleReturnToAgent = useCallback(async (bookId: string) => {
    try {
      const { error: bookError } = await supabase
        .from('scanned_books')
        .update({ quality_status: 'needs_review', scan_completed: false })
        .eq('id', bookId);

      if (bookError) throw bookError;

      await supabase
        .from('book_scan_requests')
        .update({
          assigned_to: null,
          status: 'pending' as never,
          last_activity_at: new Date().toISOString(),
        })
        .eq('resolved_book_id', bookId);

      toast.success('Returned to agent queue');
      loadBooks();
    } catch (err) {
      console.error('Error returning book:', err);
      toast.error('Failed to return book to queue');
    }
  }, [loadBooks]);

  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);

  const handleRegenerate = useCallback(async (bookId: string) => {
    setRegeneratingId(bookId);
    try {
      const { data: reqRow, error: reqErr } = await supabase
        .from('knowledge_article_requests')
        .select('id')
        .eq('generated_book_id', bookId)
        .maybeSingle();

      if (reqErr || !reqRow) {
        toast.error('No linked knowledge request found');
        return;
      }

      await supabase
        .from('knowledge_article_requests')
        .update({ status: 'pending', generated_book_id: null })
        .eq('id', reqRow.id);

      const { error: fnErr } = await supabase.functions.invoke('generate-knowledge-article', {
        body: { action: 'generate', requestId: reqRow.id },
      });

      if (fnErr) throw fnErr;
      toast.success('Article regenerated - refreshing...');
      loadBooks();
    } catch (err) {
      console.error('[ReviewQueue] Regenerate error:', err);
      toast.error('Failed to regenerate article');
    } finally {
      setRegeneratingId(null);
    }
  }, [loadBooks]);

  // -------------------------------------------------------------------------
  // Filtering
  // -------------------------------------------------------------------------

  const counts = useMemo(() => ({
    all: books.length,
    turn_codes_pending: books.filter(b => b.qualityStatus === 'turn_codes_pending').length,
    needs_review: books.filter(b => b.qualityStatus === 'needs_review').length,
  }), [books]);

  const filteredBooks = useMemo(() => {
    if (filterTab === 'all') return books;
    return books.filter(b => b.qualityStatus === filterTab);
  }, [books, filterTab]);

  // -------------------------------------------------------------------------
  // Render: detail view
  // -------------------------------------------------------------------------

  const selectedBook = books.find(b => b.id === selectedBookId);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (selectedBook) {
    const isPending = selectedBook.qualityStatus === 'turn_codes_pending';
    return (
      <>
        <BookReviewDetail
          book={selectedBook}
          colorMappings={colorMappings}
          isPending={isPending}
          onBack={handleBack}
          onApprove={() => { handleApprove(selectedBook.id); handleApproveStep(selectedBook.id, 'turn_codes'); }}
          onReturnToAgent={() => handleReturnToAgent(selectedBook.id)}
          onWordSaved={(word, turnCode, skillId) => handleWordSaved(selectedBook.id, word, turnCode, skillId)}
          onReportRefresh={() => loadReportForBook(selectedBook.id)}
        />
        {renderLexiconModal()}
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Render: list view
  // -------------------------------------------------------------------------

  const filterTabs: { id: FilterTab; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: counts.all },
    { id: 'turn_codes_pending', label: 'Turn Codes Pending', count: counts.turn_codes_pending },
    { id: 'needs_review', label: 'Needs Review', count: counts.needs_review },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-[#1A1A1A]">Review Queue</h2>
          <p className="text-sm text-[#888888] mt-1">
            Books awaiting admin review before parent availability
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="self-start md:self-auto min-h-[44px] md:min-h-0"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 mb-6">
        {filterTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilterTab(tab.id)}
            className={`px-3 py-2 md:py-1.5 rounded-lg text-xs font-medium transition-colors min-h-[44px] md:min-h-0 ${
              filterTab === tab.id
                ? 'bg-[#0A84FF] text-white'
                : 'bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05]'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Empty state */}
      {filteredBooks.length === 0 ? (
        <EmptyState
          icon={<CheckCircle className="w-12 h-12 text-[#30D158]" />}
          title="All clear"
          description="No books awaiting review."
        />
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="md:hidden space-y-2">
            {filteredBooks.map(book => (
              <Card key={book.id} padding="sm">
                <button
                  onClick={() => handleSelectBook(book.id)}
                  className="w-full flex items-center gap-3 text-left min-h-[44px]"
                >
                  {book.coverImageUrl ? (
                    <img src={book.coverImageUrl} alt="" className="w-10 h-14 object-cover rounded shrink-0" />
                  ) : (
                    <div className="w-10 h-14 bg-black/[0.05] rounded flex items-center justify-center shrink-0">
                      <BookOpen className="w-5 h-5 text-[#D4D4D4]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A1A1A] truncate">{book.title}</p>
                    {book.author && <p className="text-xs text-[#888888] truncate">{book.author}</p>}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {book.contentSource === 'generated' && (
                        <Badge variant="info"><Lightbulb className="w-3 h-3 inline mr-1" />Knowledge</Badge>
                      )}
                      {book.qualityStatus === 'turn_codes_pending' ? (
                        <Badge variant="warning">Turn Codes</Badge>
                      ) : (
                        <Badge variant="info">Needs Review</Badge>
                      )}
                      {book.contentSource === 'generated' && book.generationMetadata?.reading_level != null && (
                        <Badge variant="default">{String(book.generationMetadata.reading_level) === 'fluent' ? 'Fluent' : 'Emerging'}</Badge>
                      )}
                    </div>
                  </div>
                  <span className="text-[#0A84FF] text-sm font-medium shrink-0">Review &rarr;</span>
                </button>
              </Card>
            ))}
          </div>

          {/* Desktop: table */}
          <Card padding="none" className="hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#D4D4D4] text-left text-xs text-[#888888] uppercase tracking-wider">
                    <th className="px-4 py-3">Book</th>
                    <th className="px-4 py-3">ISBN</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredBooks.map(book => (
                    <tr key={book.id} className="hover:bg-black/[0.03] transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          {book.coverImageUrl ? (
                            <img src={book.coverImageUrl} alt="" className="w-10 h-14 object-cover rounded" />
                          ) : (
                            <div className="w-10 h-14 bg-black/[0.05] rounded flex items-center justify-center">
                              <BookOpen className="w-5 h-5 text-[#D4D4D4]" />
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-[#1A1A1A]">{book.title}</p>
                            {book.author && <p className="text-xs text-[#888888]">{book.author}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-[#3D3D3D]">{book.isbn || '—'}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          {book.contentSource === 'generated' && (
                            <Badge variant="info"><Lightbulb className="w-3 h-3 inline mr-1" />Knowledge</Badge>
                          )}
                          {book.qualityStatus === 'turn_codes_pending' ? (
                            <Badge variant="warning">Turn Codes Pending</Badge>
                          ) : (
                            <Badge variant="info">Needs Review</Badge>
                          )}
                          {book.contentSource === 'generated' && book.generationMetadata?.reading_level != null && (
                            <Badge variant="default">{String(book.generationMetadata.reading_level) === 'fluent' ? 'Fluent' : 'Emerging'}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-[#3D3D3D]">
                          {book.createdAt ? new Date(book.createdAt).toLocaleDateString() : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {book.contentSource === 'generated' && (
                            <button
                              onClick={() => handleRegenerate(book.id)}
                              disabled={regeneratingId === book.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0A84FF]/10 text-[#0A84FF] hover:bg-[#0A84FF]/20 text-sm font-medium transition-colors disabled:opacity-50"
                            >
                              {regeneratingId === book.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <RotateCcw className="w-3.5 h-3.5" />
                              )}
                              Regenerate
                            </button>
                          )}
                          {/* 3-Step Review Progress */}
                          {(() => {
                            const s1 = !!(book as ReviewBook).lexiconApprovedAt;
                            const s2 = !!(book as ReviewBook).turnCodesApprovedAt;
                            const s3 = !!(book as ReviewBook).renderApprovedAt;
                            const allDone = s1 && s2 && s3;
                            const isApproving = approvingId === book.id;

                            if (allDone && (book as ReviewBook).superadminApproved) {
                              return (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#34C759]/10 text-[#34C759] text-xs font-medium">
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  Approved
                                </span>
                              );
                            }

                            return (
                              <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                                {/* Step 1: Lexicon */}
                                {s1 ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#34C759]/10 text-[#34C759] text-[11px] font-medium">
                                    <CheckCircle className="w-3 h-3" /> Lexicon
                                  </span>
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleViewLexicon(book.id); }}
                                    disabled={isApproving}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#1A1A1A] text-[#1A1A1A] text-[11px] font-medium hover:bg-[#1A1A1A] hover:text-white transition-colors disabled:opacity-30"
                                  >
                                    1. Lexicon
                                  </button>
                                )}

                                <span className="text-[#D4D4D4] text-xs">→</span>

                                {/* Step 2: Turn Codes */}
                                {s2 ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#34C759]/10 text-[#34C759] text-[11px] font-medium">
                                    <CheckCircle className="w-3 h-3" /> Codes
                                  </span>
                                ) : s1 ? (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleSelectBook(book.id); }}
                                    disabled={isApproving}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#1A1A1A] text-[#1A1A1A] text-[11px] font-medium hover:bg-[#1A1A1A] hover:text-white transition-colors disabled:opacity-30"
                                  >
                                    2. Codes
                                  </button>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/[0.03] text-[#D4D4D4] text-[11px] font-medium">
                                    2. Codes
                                  </span>
                                )}

                                <span className="text-[#D4D4D4] text-xs">→</span>

                                {/* Step 3: Render */}
                                {s3 ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#34C759]/10 text-[#34C759] text-[11px] font-medium">
                                    <CheckCircle className="w-3 h-3" /> Render
                                  </span>
                                ) : (s1 && s2) ? (
                                  <span className="inline-flex items-center gap-1">
                                    <Link
                                      to={`/scan-agent/edit/${book.id}`}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/[0.03] text-[#1A1A1A] text-[11px] font-medium hover:bg-black/[0.05] transition-colors"
                                    >
                                      Preview
                                    </Link>
                                    <button
                                      onClick={() => handleApproveStep(book.id, 'render')}
                                      disabled={isApproving}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#1A1A1A] text-[#1A1A1A] text-[11px] font-medium hover:bg-[#1A1A1A] hover:text-white transition-colors disabled:opacity-30"
                                    >
                                      3. Approve Render
                                    </button>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/[0.03] text-[#D4D4D4] text-[11px] font-medium">
                                    3. Render
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                          <Link
                            to={`/scan-agent/edit/${book.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] hover:text-[#1A1A1A] text-sm font-medium transition-colors"
                          >
                            <Pencil className="w-3.5 h-3.5" /> Edit
                          </Link>
                          <button
                            onClick={() => handleReturnToAgent(book.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] hover:text-[#1A1A1A] text-sm font-medium transition-colors"
                            title="Return to agent queue"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
      {renderLexiconModal()}
    </div>
  );
}

// =============================================================================
// BOOK REVIEW DETAIL (Turn Code Review)
// =============================================================================

// =============================================================================
// KNOWLEDGE EDIT CHAT
// =============================================================================

interface ChatMessage {
  role: 'system' | 'admin' | 'assistant';
  content: string;
  title?: string;
  star_word?: string;
  takeaway?: string;
  timestamp: number;
}

function KnowledgeEditPanel({ bookId, onEdited }: { bookId: string; onEdited: () => void }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const chatEndRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('scanned_books')
        .select('title, reconstructed_text, generation_metadata')
        .eq('id', bookId)
        .maybeSingle();

      if (data?.reconstructed_text) {
        const meta = data.generation_metadata as Record<string, unknown> | null;
        setMessages([{
          role: 'system',
          content: data.reconstructed_text,
          title: data.title,
          star_word: meta?.star_word as string | undefined,
          takeaway: meta?.takeaway as string | undefined,
          timestamp: Date.now(),
        }]);
      }
      setLoading(false);
    })();
  }, [bookId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const instruction = input.trim();
    setInput('');

    setMessages(prev => [...prev, { role: 'admin', content: instruction, timestamp: Date.now() }]);
    setSending(true);

    try {
      const { data, error: fnErr } = await supabase.functions.invoke('generate-knowledge-article', {
        body: { action: 'edit', bookId, instruction },
      });

      if (fnErr) throw fnErr;

      if (data?.status === 'success') {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.body,
          title: data.title,
          star_word: data.star_word,
          takeaway: data.takeaway,
          timestamp: Date.now(),
        }]);
        onEdited();
        toast.success('Article updated');
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Error: ${data?.error || 'Edit failed'}`,
          timestamp: Date.now(),
        }]);
      }
    } catch (err) {
      console.error('[KnowledgeEditChat] Error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Failed to process edit. Try again.',
        timestamp: Date.now(),
      }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  if (loading) return null;
  if (messages.length === 0) return null;

  return (
    <Card className="mb-6">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <Wand2 className="w-4 h-4 text-[#0A84FF]" />
          <h3 className="text-sm font-semibold text-[#1A1A1A]">AI Editor</h3>
          <span className="text-xs text-[#888888]">{messages.length - 1} edits</span>
        </div>
        <span className="text-[#D4D4D4] text-xs">{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* Chat messages */}
          <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
            {messages.map((msg, i) => (
              <div key={i} className={`rounded-lg p-3 text-sm ${
                msg.role === 'admin'
                  ? 'bg-[#0A84FF]/15 border border-[#0A84FF]/20 ml-8'
                  : msg.role === 'system'
                  ? 'bg-black/[0.03] border border-[#D4D4D4]'
                  : 'bg-black/[0.03] border border-[#D4D4D4] mr-8'
              }`}>
                {msg.role === 'admin' ? (
                  <p className="text-[#0A84FF] text-xs font-medium mb-1">You</p>
                ) : msg.role === 'system' ? (
                  <p className="text-[#888888] text-xs font-medium mb-1">Current article</p>
                ) : (
                  <p className="text-[#30D158] text-xs font-medium mb-1">Claude</p>
                )}
                {msg.title && msg.role !== 'admin' && (
                  <p className="font-medium text-[#1A1A1A] mb-1">{msg.title}</p>
                )}
                <p className="text-[#1A1A1A] leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                {msg.star_word && (
                  <p className="text-xs text-[#0A84FF] mt-2">Star word: {msg.star_word}</p>
                )}
                {msg.takeaway && (
                  <p className="text-xs text-[#888888] mt-1">Takeaway: {msg.takeaway}</p>
                )}
              </div>
            ))}
            {sending && (
              <div className="rounded-lg p-3 bg-black/[0.03] border border-[#D4D4D4] mr-8">
                <p className="text-[#30D158] text-xs font-medium mb-1">Claude</p>
                <div className="flex items-center gap-2 text-[#888888] text-sm">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Editing article...
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
              placeholder="Tell Claude what to change..."
              className="flex-1 px-3 py-2.5 bg-black/[0.03] border border-[#D4D4D4] rounded-lg text-[#1A1A1A] text-sm placeholder-[#D4D4D4] focus:outline-none focus:border-[#0A84FF]"
              disabled={sending}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#0A84FF] text-[#1A1A1A] text-sm font-medium transition-colors disabled:opacity-40 hover:brightness-110 min-w-[72px] justify-center"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

// =============================================================================
// BOOK REVIEW DETAIL
// =============================================================================

interface BookReviewDetailProps {
  book: ReviewBook;
  colorMappings: TurnColorMapping[];
  isPending: boolean;
  onBack: () => void;
  onApprove: () => void;
  onReturnToAgent: () => void;
  onWordSaved: (word: string, turnCode: string, skillId?: string) => void;
  onReportRefresh: () => void;
}

function BookReviewDetail({
  book,
  colorMappings,
  isPending,
  onBack,
  onApprove,
  onReturnToAgent,
  onWordSaved,
  onReportRefresh,
}: BookReviewDetailProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'missing' | 'in_lexicon'>('missing');
  const [editingWord, setEditingWord] = useState<string | null>(null);
  const [editTurnCode, setEditTurnCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [editSkillId, setEditSkillId] = useState<string>('');
  const [skills, setSkills] = useState<SkillOption[]>([]);

  useEffect(() => {
    supabase
      .from('skill_sequence')
      .select('skill_id, color, phase, display_name, sequence_order')
      .order('sequence_order')
      .then(({ data }) => {
        if (data) {
          setSkills(data.map(s => ({
            skill_id: s.skill_id,
            color: s.color,
            phase: s.phase,
            display_name: s.display_name,
            sequence_order: s.sequence_order,
          })));
        }
      });
  }, []);

  const report = book.report;

  const uniqueWords = useMemo(() => {
    if (!report) return [];
    const seen = new Map<string, WordTurnCodeStatus>();
    for (const w of report.words) {
      if (!seen.has(w.cleanWord)) seen.set(w.cleanWord, w);
    }
    return Array.from(seen.values());
  }, [report]);

  const filteredWords = useMemo(() => {
    let list = uniqueWords;
    if (filterMode === 'missing') list = list.filter(w => !w.inLexicon);
    if (filterMode === 'in_lexicon') list = list.filter(w => w.inLexicon);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(w => w.cleanWord.includes(q));
    }
    return list.sort((a, b) => a.cleanWord.localeCompare(b.cleanWord));
  }, [uniqueWords, filterMode, searchQuery]);

  // -------------------------------------------------------------------------
  // Inline edit
  // -------------------------------------------------------------------------

  const handleStartEdit = (word: WordTurnCodeStatus) => {
    setEditingWord(word.cleanWord);
    const tc = word.turnCode || suggestTurnCode(word.cleanWord);
    setEditTurnCode(tc);
    setEditSkillId(word.skillId || suggestSkillId(tc, skills) || '');
  };

  const handleColorChipClick = (colorName: string) => {
    setEditTurnCode(prev => prev.replace('___', colorName));
  };

  const remainingPlaceholders = (editTurnCode.match(/___/g) || []).length;

  const handleCancelEdit = () => {
    setEditingWord(null);
    setEditTurnCode('');
    setEditSkillId('');
  };

  const handleSave = async () => {
    if (!editingWord || !editTurnCode.trim()) return;
    setSaving(true);
    const savedWord = editingWord;
    const savedCode = editTurnCode.trim();
    const result = await saveTurnCode(savedWord, savedCode, editSkillId || undefined);
    setSaving(false);

    if (result.success) {
      toast.success(`Saved turn code for "${savedWord}"`);
      setEditingWord(null);
      setEditTurnCode('');
      setEditSkillId('');
      onWordSaved(savedWord, savedCode, editSkillId || undefined);
    } else {
      toast.error(result.error || 'Failed to save');
    }
  };

  const handleApproveClick = async () => {
    setApproving(true);
    await onApprove();
    setApproving(false);
  };

  useEffect(() => {
    if (!editingWord || !editTurnCode.trim()) return;
    if (editSkillId) return;
    const suggestion = suggestSkillId(editTurnCode, skills);
    if (suggestion) setEditSkillId(suggestion);
  }, [editTurnCode, skills]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Turn code preview
  // -------------------------------------------------------------------------

  const renderTurnCodePreview = (turnCode: string) => {
    try {
      const segments = parseTurnCode(turnCode);
      return (
        <span className="inline-flex gap-px">
          {segments.map((seg, i) => {
            if (seg.type === 'color' && seg.color) {
              const hex = getHexColor(seg.color, colorMappings);
              return (
                <span key={i} style={{ color: hex || '#fff' }} className="font-mono font-bold">
                  {seg.content}
                </span>
              );
            }
            return <span key={i} className="font-mono text-[#1A1A1A]">{seg.content}</span>;
          })}
        </span>
      );
    } catch {
      return <span className="text-[#FF453A] font-mono text-xs">Invalid</span>;
    }
  };

  if (book.reportLoading || !report) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Shared inline-edit UI
  // -------------------------------------------------------------------------

  const renderEditInput = () => (
    <div className="space-y-2">
      <input
        type="text"
        value={editTurnCode}
        onChange={e => setEditTurnCode(e.target.value)}
        placeholder="e.g. h, [e-green], l, p"
        className="w-full px-3 py-2 bg-black/[0.03] border border-[#0A84FF] rounded text-[#1A1A1A] text-sm font-mono focus:outline-none"
        autoFocus
        onKeyDown={e => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') handleCancelEdit();
        }}
      />
      {remainingPlaceholders > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {colorMappings.map(cm => (
            <button
              key={cm.color_name}
              onClick={() => handleColorChipClick(cm.color_name)}
              className="min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 px-2 py-1.5 md:px-1.5 md:py-0.5 rounded text-xs md:text-[10px] font-bold bg-black/[0.03] hover:bg-black/[0.06] active:bg-black/[0.08] transition-colors leading-tight flex items-center justify-center"
              title={cm.color_name}
              style={{ color: cm.hex_code }}
            >
              {cm.color_name.slice(0, 2).toUpperCase()}
            </button>
          ))}
        </div>
      )}
      {FEATURES.SKILL_PROGRESSION && (
        <select
          value={editSkillId}
          onChange={e => setEditSkillId(e.target.value)}
          className="w-full px-3 py-2 bg-black/[0.03] border border-[#D4D4D4] rounded text-[#1A1A1A] text-sm focus:outline-none focus:border-[#0A84FF] appearance-none"
        >
          <option value="" className="bg-[#1a1a2e]">Unassigned</option>
          {['1', '2', '3', 'r'].map(phase => {
            const phaseSkills = skills.filter(s => s.phase === phase);
            if (phaseSkills.length === 0) return null;
            return (
              <optgroup key={phase} label={`Phase ${phase === 'r' ? 'Review' : phase}`} className="bg-[#1a1a2e]">
                {phaseSkills.map(s => (
                  <option key={s.skill_id} value={s.skill_id} className="bg-[#1a1a2e]">
                    {s.skill_id} — {s.display_name}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>
      )}
      {editTurnCode.trim() && (
        <div className="flex items-center gap-2 text-xs text-[#888888]">
          <span>Preview:</span> {renderTurnCodePreview(editTurnCode)}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          onClick={handleSave}
          disabled={saving || !editTurnCode.trim()}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#30D158]/20 text-[#30D158] text-sm font-medium transition-colors disabled:opacity-30 min-h-[44px]"
        >
          {saving ? <Spinner size="sm" /> : <Check className="w-4 h-4" />}
          Save
        </button>
        <button
          onClick={handleCancelEdit}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/[0.03] text-[#3D3D3D] text-sm font-medium transition-colors min-h-[44px]"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-6">
        <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-3">
          <button
            onClick={onBack}
            className="text-[#888888] hover:text-[#1A1A1A] transition-colors text-sm self-start min-h-[44px] flex items-center"
          >
            &larr; Back to queue
          </button>
          <span className="hidden md:inline text-[#D4D4D4]">|</span>
          <div>
            <h2 className="text-lg font-semibold text-[#1A1A1A]">{book.title}</h2>
            <div className="flex items-center gap-2">
              {book.author && <span className="text-[#888888] text-sm">by {book.author}</span>}
              {book.qualityStatus === 'turn_codes_pending' ? (
                <Badge variant="warning">Turn Codes Pending</Badge>
              ) : book.qualityStatus === 'ready' ? (
                <Badge variant="success">Published</Badge>
              ) : (
                <Badge variant="info">Needs Review</Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start md:self-auto">
          <Link
            to={`/scan-agent/edit/${book.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] hover:text-[#1A1A1A] text-sm font-medium transition-colors min-h-[44px]"
          >
            <ExternalLink className="w-4 h-4" /> Edit Content
          </Link>
          <button
            onClick={onReturnToAgent}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] hover:text-[#1A1A1A] text-sm font-medium transition-colors min-h-[44px]"
          >
            <RotateCcw className="w-4 h-4" /> Return to Agent
          </button>
          {isPending && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleApproveClick}
              disabled={approving}
              className="min-h-[44px]"
            >
              {approving ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve &amp; Publish
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 mb-6">
        <Card padding="sm">
          <p className="text-2xl font-bold text-[#1A1A1A]">{report.uniqueWords}</p>
          <p className="text-xs text-[#888888]">Unique words</p>
        </Card>
        <Card padding="sm">
          <p className="text-2xl font-bold text-[#30D158]">{report.matchedWords}</p>
          <p className="text-xs text-[#888888]">In lexicon</p>
        </Card>
        <Card padding="sm">
          <p className="text-2xl font-bold text-[#FF9F0A]">{report.missingWords}</p>
          <p className="text-xs text-[#888888]">Missing</p>
        </Card>
        <Card padding="sm">
          <p className={`text-2xl font-bold ${report.coveragePct === 100 ? 'text-[#30D158]' : 'text-[#FF9F0A]'}`}>
            {report.coveragePct}%
          </p>
          <p className="text-xs text-[#888888]">Coverage</p>
        </Card>
      </div>

      {/* AI Edit (knowledge articles only) */}
      {book.contentSource === 'generated' && (
        <KnowledgeEditPanel bookId={book.id} onEdited={onReportRefresh} />
      )}

      {/* Filter bar */}
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center mb-4">
        <div className="relative flex-1 md:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
          <input
            type="text"
            placeholder="Search words..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 md:py-2 bg-black/[0.03] border border-[#D4D4D4] rounded-lg text-[#1A1A1A] text-sm placeholder-[#D4D4D4] focus:outline-none focus:border-[#0A84FF]"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {(['missing', 'all', 'in_lexicon'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => setFilterMode(mode)}
                className={`px-3 py-2 md:py-1.5 rounded-lg text-xs font-medium transition-colors min-h-[44px] md:min-h-0 ${
                  filterMode === mode
                    ? 'bg-[#0A84FF] text-white'
                    : 'bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05]'
                }`}
              >
                {mode === 'missing' ? 'Missing' : mode === 'in_lexicon' ? 'In Lexicon' : 'All'}
              </button>
            ))}
          </div>
          <Button variant="secondary" size="sm" onClick={onReportRefresh} className="min-h-[44px] md:min-h-0">
            <RefreshCw className="w-3 h-3 mr-1" /> Reload
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {filteredWords.length === 0 && (
        <Card>
          <p className="text-center text-[#888888] text-sm py-4">
            {filterMode === 'missing' ? 'No missing words — all covered!' : 'No words match your filter.'}
          </p>
        </Card>
      )}

      {/* Mobile: card layout */}
      {filteredWords.length > 0 && (
        <div className="md:hidden space-y-2">
          {filteredWords.map(w => {
            const isEditing = editingWord === w.cleanWord;
            const suggestion = !w.inLexicon ? suggestTurnCode(w.cleanWord) : null;

            return (
              <Card key={w.cleanWord} padding="sm">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-base font-semibold text-[#1A1A1A]">{w.cleanWord}</span>
                    {w.inLexicon ? <Badge variant="success">In Lexicon</Badge> : <Badge variant="warning">Missing</Badge>}
                  </div>
                  {!isEditing && (
                    <button
                      onClick={() => handleStartEdit(w)}
                      className="p-2.5 rounded-lg hover:bg-black/[0.05] text-[#888888] hover:text-[#1A1A1A] transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {isEditing ? renderEditInput() : (
                  <div className="space-y-1">
                    {w.turnCode ? (
                      <>
                        <p className="font-mono text-xs text-[#3D3D3D] break-all">{w.turnCode}</p>
                        <div className="flex items-center gap-2 text-xs text-[#888888]">
                          <span>Preview:</span> {renderTurnCodePreview(w.turnCode)}
                        </div>
                      </>
                    ) : suggestion ? (
                      <button
                        onClick={() => handleStartEdit(w)}
                        className="flex items-center gap-1.5 font-mono text-xs text-[#BF5AF2]/70 active:text-[#BF5AF2] transition-colors min-h-[44px]"
                      >
                        <Wand2 className="w-3.5 h-3.5 shrink-0" />
                        <span className="break-all text-left">{suggestion}</span>
                      </button>
                    ) : null}
                    {w.skillId && <p className="text-xs text-[#888888]">Skill: {w.skillId}</p>}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Desktop: table layout */}
      {filteredWords.length > 0 && (
        <Card padding="none" className="hidden md:block">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#D4D4D4] text-left text-xs text-[#888888] uppercase tracking-wider">
                  <th className="px-4 py-3">Word</th>
                  <th className="px-4 py-3">Turn Code</th>
                  <th className="px-4 py-3">Preview</th>
                  <th className="px-4 py-3">Skill ID</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredWords.map(w => {
                  const isEditing = editingWord === w.cleanWord;
                  const suggestion = !w.inLexicon ? suggestTurnCode(w.cleanWord) : null;

                  return (
                    <tr key={w.cleanWord} className="hover:bg-black/[0.03] transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-[#1A1A1A]">{w.cleanWord}</span>
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div className="space-y-1.5">
                            <input
                              type="text"
                              value={editTurnCode}
                              onChange={e => setEditTurnCode(e.target.value)}
                              placeholder="e.g. h, [e-green], l, p"
                              className="w-full px-2 py-1 bg-black/[0.03] border border-[#0A84FF] rounded text-[#1A1A1A] text-xs font-mono focus:outline-none"
                              autoFocus
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleSave();
                                if (e.key === 'Escape') handleCancelEdit();
                              }}
                            />
                            {remainingPlaceholders > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {colorMappings.map(cm => (
                                  <button
                                    key={cm.color_name}
                                    onClick={() => handleColorChipClick(cm.color_name)}
                                    className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-black/[0.03] hover:bg-black/[0.06] transition-colors leading-tight"
                                    title={cm.color_name}
                                    style={{ color: cm.hex_code }}
                                  >
                                    {cm.color_name.slice(0, 2).toUpperCase()}
                                  </button>
                                ))}
                              </div>
                            )}
                            {FEATURES.SKILL_PROGRESSION && (
                              <select
                                value={editSkillId}
                                onChange={e => setEditSkillId(e.target.value)}
                                className="w-full px-2 py-1 bg-black/[0.03] border border-[#D4D4D4] rounded text-[#1A1A1A] text-xs focus:outline-none focus:border-[#0A84FF] appearance-none"
                              >
                                <option value="" className="bg-[#1a1a2e]">Unassigned</option>
                                {['1', '2', '3', 'r'].map(phase => {
                                  const phaseSkills = skills.filter(s => s.phase === phase);
                                  if (phaseSkills.length === 0) return null;
                                  return (
                                    <optgroup key={phase} label={`Phase ${phase === 'r' ? 'Review' : phase}`} className="bg-[#1a1a2e]">
                                      {phaseSkills.map(s => (
                                        <option key={s.skill_id} value={s.skill_id} className="bg-[#1a1a2e]">
                                          {s.skill_id} — {s.display_name}
                                        </option>
                                      ))}
                                    </optgroup>
                                  );
                                })}
                              </select>
                            )}
                          </div>
                        ) : w.turnCode ? (
                          <span className="font-mono text-xs text-[#3D3D3D]">{w.turnCode}</span>
                        ) : suggestion ? (
                          <button
                            onClick={() => handleStartEdit(w)}
                            className="group flex items-center gap-1.5 font-mono text-xs text-[#BF5AF2]/70 hover:text-[#BF5AF2] transition-colors"
                            title="Click to edit suggested turn code"
                          >
                            <Wand2 className="w-3 h-3 shrink-0" />
                            <span className="truncate max-w-[200px]">{suggestion}</span>
                          </button>
                        ) : (
                          <span className="font-mono text-xs text-[#D4D4D4]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing && editTurnCode.trim()
                          ? renderTurnCodePreview(editTurnCode)
                          : w.turnCode
                            ? renderTurnCodePreview(w.turnCode)
                            : suggestion
                              ? renderTurnCodePreview(suggestion)
                              : <span className="text-[#D4D4D4] text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-[#888888]">{w.skillId || '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        {w.inLexicon ? <Badge variant="success">In Lexicon</Badge> : <Badge variant="warning">Missing</Badge>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={handleSave}
                              disabled={saving || !editTurnCode.trim()}
                              className="p-1.5 rounded hover:bg-[#30D158]/20 text-[#30D158] transition-colors disabled:opacity-30"
                              title="Save"
                            >
                              {saving ? <Spinner size="sm" /> : <Check className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="p-1.5 rounded hover:bg-[#FF453A]/20 text-[#FF453A] transition-colors"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleStartEdit(w)}
                            className="p-1.5 rounded hover:bg-black/[0.05] text-[#888888] hover:text-[#1A1A1A] transition-colors"
                            title="Edit turn code"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export default ReviewQueuePage;
